import { area as d3Area, curveMonotoneX, line as d3Line } from 'd3-shape';

import { useChartContext, yScaleFor } from '../core/chart-context';
import type { NumericSeries } from '../core/types';

export interface LinePathProps {
  series: NumericSeries[];
  /** Renders a dot at every defined point */
  dots?: boolean;
  strokeWidth?: number;
  strokeOpacity?: number;
  /** Index of the hovered x-slot, enlarged as the active dot */
  activeIndex?: number | null;
  /** Overrides the series color for every line */
  stroke?: string;
  /** Fills the region between the line and the zero baseline */
  area?: boolean;
  /** Fill opacity of the area region */
  areaOpacity?: number;
}

interface LinePoint {
  index: number;
  value: number;
}

/** Monotone line per series with optional dots and area fill. */
export const LinePath = ({
  series,
  dots = true,
  strokeWidth = 2.5,
  strokeOpacity,
  activeIndex,
  stroke,
  area = false,
  areaOpacity = 0.15
}: LinePathProps) => {
  const context = useChartContext();
  const { xScale } = context;
  const domain = xScale.domain;

  return (
    <g data-slot='chart-lines'>
      {series.map((s, seriesIndex) => {
        const yScale = yScaleFor(context, s.axis);
        const toPath = d3Line<LinePoint>()
          .x(point => xScale.position(domain[point.index]))
          .y(point => yScale(point.value))
          .curve(curveMonotoneX);
        const toArea = d3Area<LinePoint>()
          .x(point => xScale.position(domain[point.index]))
          .y0(yScale(0))
          .y1(point => yScale(point.value))
          .curve(curveMonotoneX);

        const points = s.values
          .map((value, index) => ({ index, value }))
          .filter((point): point is LinePoint => point.value !== undefined);
        const color = stroke ?? s.color;

        return (
          <g key={`${seriesIndex}-${s.label}`}>
            {area && (
              <path
                data-slot='chart-area'
                d={toArea(points) ?? undefined}
                fill={color}
                fillOpacity={areaOpacity}
                stroke='none'
              />
            )}
            <path
              data-slot='chart-line'
              d={toPath(points) ?? undefined}
              fill='none'
              stroke={color}
              strokeOpacity={strokeOpacity}
              strokeWidth={strokeWidth}
              strokeLinecap='round'
            />
            {dots &&
              points.map(point => (
                <circle
                  key={`${domain[point.index]}`}
                  data-slot='chart-dot'
                  cx={xScale.position(domain[point.index])}
                  cy={yScale(point.value)}
                  r={activeIndex === point.index ? 5 : 3}
                  fill={color}
                />
              ))}
          </g>
        );
      })}
    </g>
  );
};

LinePath.displayName = 'LinePath';
